import { Armchair } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { useNow } from '@/components/allocation/use-now';
import { holdClock } from '@/components/allocation/types';

// The seat-hold clock for a re-accommodation the operator has SELECTED but not
// yet ticketed. Until now flights carried no hold signal at all: the operator
// could see a hotel room counting down but had nothing telling them the seats
// were also on a clock.
//
// `holdExpiresAt` is server truth off the rebooking GROUP VIEW (the options
// path) — never derived here, and never synthesized when absent. Three states,
// same discipline as the hotel hold:
//   • ticketed / no clock → render nothing. On a SELECTED group an absent
//     `holdExpiresAt` means ticketed or hold gone — the ticket is the durable
//     signal, and a missing deadline is never a lapsed one.
//   • counting            → "Seats held · 12m 30s · until 14:32".
//   • lapsed              → neutral, informational. The seats are re-checked at
//     accept, so this is a heads-up, not a failure.

interface SeatHoldChipProps {
  /** Server-truth seat-hold deadline (ISO) from the rebooking group view. */
  holdExpiresAt?: string | undefined;
  /** A ticket is issued — the hold is moot, so the chip stands down. */
  isTicketed?: boolean;
  className?: string;
}

export function SeatHoldChip({ holdExpiresAt, isTicketed = false, className }: SeatHoldChipProps) {
  const { t } = useTranslation();
  // Ticks only while there is a live deadline to render; a render tick, not a
  // poll — state changes arrive over the existing WS invalidation.
  const now = useNow(!!holdExpiresAt && !isTicketed);

  if (isTicketed || !holdExpiresAt) return null;
  const clock = holdClock(holdExpiresAt, now);
  if (!clock.hasClock) return null;

  if (clock.lapsed) {
    return (
      <Badge
        variant="neutral"
        size="sm"
        className={className}
        data-testid="seat-hold-chip"
        data-clock="lapsed"
      >
        <Armchair className="h-3 w-3" aria-hidden />
        {t('holds.seat.lapsed', 'Seat hold lapsed — re-checked at accept')}
      </Badge>
    );
  }

  return (
    <Badge
      variant="info"
      size="sm"
      className={className}
      data-testid="seat-hold-chip"
      data-clock="counting"
      title={`${t('holds.clock.until', 'until')} ${clock.until}`}
    >
      <Armchair className="h-3 w-3" aria-hidden />
      <span className="tabular-nums">
        {t('holds.seat.held', 'Seats held')} · {clock.remaining}
      </span>
      <span className="font-normal opacity-70">
        · {t('holds.clock.until', 'until')} {clock.until}
      </span>
    </Badge>
  );
}
